import React, { useState } from 'react'
import { Box, styled, Tab, TabProps, Tabs, useTheme } from '@mui/material'

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: 'unset'
}))

export const StyledTab = styled(Tab)<TabProps>(({ theme }) => ({
  minHeight: 'unset',
  display: 'flex',
  flexDirection: 'row',
  alignContent: 'center',
  gap: theme.spacing(1),
  textTransform: 'none',
  border: `1px solid ${theme.palette.tabBorder.main}`,
  fontSize: '16px !important',
  paddingInline: theme.spacing(3),
  borderRadius: '8px 8px 0 0',
  '&.selected': {
    background: theme.palette.primarySoft.main,
    color: theme.palette.primarySoft.contrastText
  },
  '& svg': {
    marginBottom: '0 !important',
    fontSize: '20px'
  }
}))

/**
 * TabNavigatorProps interface defines the properties for the TabNavigator component.
 */
interface TabNavigatorProps extends TabProps {
  /** Array of pages to display in the tab navigator */
  pages?: Array<{
    pageTitle: string
    child: React.ReactNode
  }>
  /** Callback function to determine if the tab change is allowed */
  onChange?: () => boolean
}

/**
 * TabNavigator component
 * This component renders a tab navigator with the given pages.
 * It allows switching between different tabs and displays the corresponding content.
 * @param {TabNavigatorProps} props - The properties for the TabNavigator component.
 * @returns {JSX.Element} The rendered TabNavigator component.
 */
const TabNavigator: React.FC<TabNavigatorProps> = ({
  pages,
  onChange
}: TabNavigatorProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<number>(0)
  const theme = useTheme()

  const handleChange = (_: any, newValue: number) => {
    const ableToChange = onChange ? onChange() : true
    if (ableToChange) {
      setActiveTab(newValue)
    }
  }

  const tabSelector = pages?.map(({ pageTitle }, index) => (
    <StyledTab
      label={pageTitle}
      key={`tab-navigator-${index}-${pageTitle}`}
      className={activeTab === index ? 'selected' : ''}
    />
  ))

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTabs
        value={activeTab}
        onChange={handleChange}
        variant='scrollable'
        scrollButtons='auto'
        aria-label='tab navigator'
        slotProps={{
          indicator: {
            style: {
              background: theme.palette.secondary.main
            }
          }
        }}
      >
        {tabSelector}
      </StyledTabs>
      {(pages ?? [])[activeTab]?.child}
    </Box>
  )
}

export default TabNavigator
